import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileCheck,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  XCircle,
  AlertTriangle,
  MessageSquare,
  Send,
  Loader2,
  Clock,
  User,
  Hash,
  Sparkles,
} from "lucide-react";
import { invoicesApi } from "../../services/api";
import AiPreBillReview from "./AiPreBillReview";

// ── Types ────────────────────────────────────────────────────────────────────

interface LineItem {
  kind: string;
  description: string;
  quantity: number;
  unitPriceCents: number;
  amountCents: number;
  taxable: boolean;
  taxAmountCents: number;
}

interface Comment {
  authorId: any;
  body: string;
  lineItemIndex: number | null;
  createdAt: string;
}

interface Invoice {
  _id: string;
  invoiceNumber: string;
  matterId: any;
  clientId: any;
  status: string;
  lineItems: LineItem[];
  subtotalCents: number;
  discountCents: number;
  taxCents: number;
  totalCents: number;
  paidCents: number;
  balanceDueCents: number;
  comments: Comment[];
  approvals: any[];
  issuedAt: string | null;
  dueAt: string | null;
  createdAt: string;
  notesToClient: string | null;
}

interface Props {
  onDataChange?: () => void;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatCurrency = (cents: number, currency = "CAD") =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency }).format(
    cents / 100
  );

const statusColors: Record<string, { bg: string; text: string }> = {
  draft: { bg: "bg-amber-50", text: "text-amber-700" },
  pending_approval: { bg: "bg-blue-50", text: "text-blue-700" },
  approved: { bg: "bg-emerald-50", text: "text-emerald-700" },
};

const formatStatus = (s: string) =>
  s
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const formatDate = (d: string) =>
  new Date(d).toLocaleDateString("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const formatDateTime = (d: string) =>
  new Date(d).toLocaleDateString("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const kindLabels: Record<string, string> = {
  time: "Time",
  fee: "Fee",
  disbursement: "Disbursement",
  tax: "Tax",
  credit: "Credit",
  discount: "Discount",
};

// ── Component ────────────────────────────────────────────────────────────────

const PreBillReviewTab: React.FC<Props> = ({ onDataChange }) => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [commentLoading, setCommentLoading] = useState(false);
  const [showAiReview, setShowAiReview] = useState(false);

  // ── Fetch ────────────────────────────────────────────────────────────────

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const [drafts, pending] = await Promise.all([
        invoicesApi.list({ status: "draft" }),
        invoicesApi.list({ status: "pending_approval" }),
      ]);

      const draftList = drafts.data?.invoices || drafts.data?.data || [];
      const pendingList = pending.data?.invoices || pending.data?.data || [];

      // Merge and deduplicate by _id
      const map = new Map<string, Invoice>();
      [...draftList, ...pendingList].forEach((inv: Invoice) => {
        map.set(inv._id, inv);
      });

      // Sort by most recently created first
      const merged = Array.from(map.values()).sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      setInvoices(merged);
    } catch (err) {
      console.error("Failed to fetch pre-bill invoices:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // ── Actions ──────────────────────────────────────────────────────────────

  const handleApprove = async (id: string) => {
    setActionLoading(`approve-${id}`);
    try {
      await invoicesApi.approve(id, { status: "approved" });
      await fetchInvoices();
      onDataChange?.();
      setSelectedInvoice(null);
    } catch (err) {
      console.error("Failed to approve invoice:", err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id: string) => {
    setActionLoading(`reject-${id}`);
    try {
      await invoicesApi.approve(id, { status: "rejected", note: comment || null });
      await fetchInvoices();
      onDataChange?.();
      setSelectedInvoice(null);
      setComment("");
    } catch (err) {
      console.error("Failed to reject invoice:", err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRequestChanges = async (id: string) => {
    setActionLoading(`changes-${id}`);
    try {
      await invoicesApi.changeStatus(id, "draft");
      if (comment.trim()) {
        // Add comment via update to persist the change request note
        const invoice = invoices.find((inv) => inv._id === id);
        if (invoice) {
          await invoicesApi.update(id, {
            comments: [
              ...(invoice.comments || []),
              { body: comment.trim(), createdAt: new Date().toISOString() },
            ],
          });
        }
      }
      await fetchInvoices();
      onDataChange?.();
      setSelectedInvoice(null);
      setComment("");
    } catch (err) {
      console.error("Failed to request changes:", err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddComment = async (id: string) => {
    if (!comment.trim()) return;
    setCommentLoading(true);
    try {
      const invoice = invoices.find((inv) => inv._id === id);
      if (invoice) {
        await invoicesApi.update(id, {
          comments: [
            ...(invoice.comments || []),
            { body: comment.trim(), createdAt: new Date().toISOString() },
          ],
        });
        setComment("");
        await fetchInvoices();
      }
    } catch (err) {
      console.error("Failed to add comment:", err);
    } finally {
      setCommentLoading(false);
    }
  };

  // ── Render: Loading ──────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--color-primary)]" />
        <span className="ml-3 text-sm text-slate-500">
          Loading review queue...
        </span>
      </div>
    );
  }

  // ── Render: Empty state ──────────────────────────────────────────────────

  if (invoices.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col items-center justify-center py-24 text-center"
      >
        <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
          <FileCheck className="w-8 h-8 text-emerald-600" />
        </div>
        <h3 className="text-lg font-semibold text-slate-800 mb-1">
          No invoices pending review
        </h3>
        <p className="text-sm text-slate-500 max-w-sm">
          All invoices have been reviewed. New drafts and invoices awaiting
          approval will appear here.
        </p>
      </motion.div>
    );
  }

  // ── Render: Review queue ─────────────────────────────────────────────────

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-4"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">
            Pre-Bill Review
          </h2>
          <p className="text-sm text-slate-500">
            {invoices.length} invoice{invoices.length !== 1 ? "s" : ""} pending
            review
          </p>
        </div>
        <button
          onClick={() => setShowAiReview((prev) => !prev)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white shadow-sm hover:shadow transition-all"
          style={{
            background: "linear-gradient(to right, var(--color-primary), var(--color-accent))",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "linear-gradient(to right, var(--color-primary-hover), var(--color-accent))";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "linear-gradient(to right, var(--color-primary), var(--color-accent))";
          }}
        >
          <Sparkles className="w-4 h-4" />
          AI Review
        </button>
      </div>

      {/* AI Pre-Bill Review Panel */}
      <AnimatePresence>
        {showAiReview && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <AiPreBillReview
              onClose={() => setShowAiReview(false)}
              onEntriesUpdated={() => fetchInvoices()}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/50">
              <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-5 py-3">
                Invoice
              </th>
              <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-5 py-3">
                Client
              </th>
              <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-5 py-3">
                Matter
              </th>
              <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-5 py-3">
                Total
              </th>
              <th className="text-center text-xs font-medium text-slate-500 uppercase tracking-wider px-5 py-3">
                Status
              </th>
              <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-5 py-3">
                Created
              </th>
              <th className="w-10 px-3 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {invoices.map((invoice) => {
              const isExpanded = selectedInvoice === invoice._id;
              const colors = statusColors[invoice.status] || {
                bg: "bg-slate-50",
                text: "text-slate-600",
              };

              return (
                <React.Fragment key={invoice._id}>
                  {/* Row */}
                  <tr
                    onClick={() =>
                      setSelectedInvoice(isExpanded ? null : invoice._id)
                    }
                    className={`cursor-pointer transition-colors ${
                      isExpanded
                        ? "bg-slate-50"
                        : "hover:bg-slate-50/60"
                    }`}
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <Hash className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-sm font-medium text-slate-800">
                          {invoice.invoiceNumber}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-sm text-slate-700">
                        {invoice.clientId?.displayName || "—"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-sm text-slate-600">
                        {invoice.matterId?.title || "—"}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <span className="text-sm font-semibold text-slate-800">
                        {formatCurrency(invoice.totalCents)}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}
                      >
                        {formatStatus(invoice.status)}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-sm text-slate-500">
                        {formatDate(invoice.createdAt)}
                      </span>
                    </td>
                    <td className="px-3 py-4 text-center">
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      )}
                    </td>
                  </tr>

                  {/* Detail Panel */}
                  <AnimatePresence>
                    {isExpanded && (
                      <tr>
                        <td colSpan={7} className="p-0">
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{
                              height: { duration: 0.3, ease: "easeInOut" },
                              opacity: { duration: 0.2 },
                            }}
                            className="overflow-hidden"
                          >
                            <div className="px-6 py-5 bg-slate-50/80 border-t border-slate-100">
                              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Left: Line items */}
                                <div className="lg:col-span-2 space-y-4">
                                  {/* Status badge */}
                                  <div className="flex items-center gap-3">
                                    <span
                                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${colors.bg} ${colors.text}`}
                                    >
                                      {invoice.status === "draft" && (
                                        <Clock className="w-3.5 h-3.5" />
                                      )}
                                      {invoice.status ===
                                        "pending_approval" && (
                                        <AlertTriangle className="w-3.5 h-3.5" />
                                      )}
                                      {formatStatus(invoice.status)}
                                    </span>
                                    {invoice.comments?.length > 0 && (
                                      <span className="text-xs text-slate-500 flex items-center gap-1">
                                        <MessageSquare className="w-3 h-3" />
                                        {invoice.comments.length} comment
                                        {invoice.comments.length !== 1
                                          ? "s"
                                          : ""}
                                      </span>
                                    )}
                                  </div>

                                  {/* Line items */}
                                  <div>
                                    <h4 className="text-sm font-semibold text-slate-700 mb-2">
                                      Line Items
                                    </h4>
                                    {invoice.lineItems?.length > 0 ? (
                                      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                                        <table className="w-full">
                                          <thead>
                                            <tr className="border-b border-slate-100">
                                              <th className="text-left text-xs font-medium text-slate-500 px-4 py-2">
                                                Type
                                              </th>
                                              <th className="text-left text-xs font-medium text-slate-500 px-4 py-2">
                                                Description
                                              </th>
                                              <th className="text-right text-xs font-medium text-slate-500 px-4 py-2">
                                                Qty
                                              </th>
                                              <th className="text-right text-xs font-medium text-slate-500 px-4 py-2">
                                                Rate
                                              </th>
                                              <th className="text-right text-xs font-medium text-slate-500 px-4 py-2">
                                                Amount
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody className="divide-y divide-slate-50">
                                            {invoice.lineItems.map(
                                              (item, idx) => (
                                                <tr
                                                  key={idx}
                                                  className="text-sm"
                                                >
                                                  <td className="px-4 py-2.5">
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
                                                      {kindLabels[item.kind] ||
                                                        item.kind}
                                                    </span>
                                                  </td>
                                                  <td className="px-4 py-2.5 text-slate-700 max-w-xs truncate">
                                                    {item.description}
                                                  </td>
                                                  <td className="px-4 py-2.5 text-right text-slate-600">
                                                    {item.quantity}
                                                  </td>
                                                  <td className="px-4 py-2.5 text-right text-slate-600">
                                                    {formatCurrency(
                                                      item.unitPriceCents
                                                    )}
                                                  </td>
                                                  <td className="px-4 py-2.5 text-right font-medium text-slate-800">
                                                    {formatCurrency(
                                                      item.amountCents
                                                    )}
                                                  </td>
                                                </tr>
                                              )
                                            )}
                                          </tbody>
                                        </table>

                                        {/* Totals */}
                                        <div className="border-t border-slate-200 px-4 py-3 space-y-1">
                                          <div className="flex justify-between text-sm text-slate-600">
                                            <span>Subtotal</span>
                                            <span>
                                              {formatCurrency(
                                                invoice.subtotalCents
                                              )}
                                            </span>
                                          </div>
                                          {invoice.discountCents > 0 && (
                                            <div className="flex justify-between text-sm text-slate-600">
                                              <span>Discount</span>
                                              <span className="text-red-600">
                                                -
                                                {formatCurrency(
                                                  invoice.discountCents
                                                )}
                                              </span>
                                            </div>
                                          )}
                                          {invoice.taxCents > 0 && (
                                            <div className="flex justify-between text-sm text-slate-600">
                                              <span>Tax</span>
                                              <span>
                                                {formatCurrency(
                                                  invoice.taxCents
                                                )}
                                              </span>
                                            </div>
                                          )}
                                          <div className="flex justify-between text-sm font-semibold text-slate-800 pt-1 border-t border-slate-100">
                                            <span>Total</span>
                                            <span>
                                              {formatCurrency(
                                                invoice.totalCents
                                              )}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="bg-white rounded-xl border border-slate-200 px-4 py-6 text-center">
                                        <p className="text-sm text-slate-500">
                                          No line items on this invoice.
                                        </p>
                                      </div>
                                    )}
                                  </div>

                                  {/* Action buttons */}
                                  <div className="flex items-center gap-3 pt-2">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleApprove(invoice._id);
                                      }}
                                      disabled={
                                        actionLoading === `approve-${invoice._id}`
                                      }
                                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 transition-colors disabled:opacity-50"
                                    >
                                      {actionLoading ===
                                      `approve-${invoice._id}` ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <CheckCircle className="w-4 h-4" />
                                      )}
                                      Approve
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRequestChanges(invoice._id);
                                      }}
                                      disabled={
                                        actionLoading ===
                                        `changes-${invoice._id}`
                                      }
                                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 transition-colors disabled:opacity-50"
                                    >
                                      {actionLoading ===
                                      `changes-${invoice._id}` ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <AlertTriangle className="w-4 h-4" />
                                      )}
                                      Request Changes
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleReject(invoice._id);
                                      }}
                                      disabled={
                                        actionLoading ===
                                        `reject-${invoice._id}`
                                      }
                                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 transition-colors disabled:opacity-50"
                                    >
                                      {actionLoading ===
                                      `reject-${invoice._id}` ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <XCircle className="w-4 h-4" />
                                      )}
                                      Reject
                                    </button>
                                  </div>
                                </div>

                                {/* Right: Comments */}
                                <div className="space-y-3">
                                  <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                                    <MessageSquare className="w-4 h-4" />
                                    Comments
                                  </h4>

                                  {/* Existing comments */}
                                  <div className="space-y-2 max-h-64 overflow-y-auto">
                                    {invoice.comments?.length > 0 ? (
                                      invoice.comments.map((c, idx) => (
                                        <div
                                          key={idx}
                                          className="bg-white rounded-xl border border-slate-200 p-3"
                                        >
                                          <div className="flex items-center gap-1.5 mb-1">
                                            <User className="w-3 h-3 text-slate-400" />
                                            <span className="text-xs font-medium text-slate-600">
                                              {c.authorId?.name ||
                                                c.authorId?.email ||
                                                "Team Member"}
                                            </span>
                                            <span className="text-xs text-slate-400 ml-auto">
                                              {c.createdAt
                                                ? formatDateTime(c.createdAt)
                                                : ""}
                                            </span>
                                          </div>
                                          <p className="text-sm text-slate-700">
                                            {c.body}
                                          </p>
                                        </div>
                                      ))
                                    ) : (
                                      <p className="text-xs text-slate-400 italic py-2">
                                        No comments yet.
                                      </p>
                                    )}
                                  </div>

                                  {/* Add comment */}
                                  <div className="flex items-start gap-2">
                                    <textarea
                                      value={comment}
                                      onChange={(e) =>
                                        setComment(e.target.value)
                                      }
                                      onClick={(e) => e.stopPropagation()}
                                      placeholder="Add a comment..."
                                      rows={2}
                                      className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600 resize-none"
                                    />
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAddComment(invoice._id);
                                      }}
                                      disabled={
                                        !comment.trim() || commentLoading
                                      }
                                      className="mt-0.5 p-2 rounded-xl text-white bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] transition-colors disabled:opacity-40"
                                    >
                                      {commentLoading ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <Send className="w-4 h-4" />
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        </td>
                      </tr>
                    )}
                  </AnimatePresence>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
};

export default PreBillReviewTab;
